import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  buscarAnual,
  reais,
  urlMovimentoCsv,
  urlPrestacaoContas,
  NOME_CATEGORIA,
  type Anual,
} from '../lib/financeiro';
import { Alerta, Cartao, LinkBotao } from './Interface';

const MESES_CURTO = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export default function FinanceiroAno({ ano }: { ano: number }) {
  const [dados, setDados] = useState<Anual | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await buscarAnual(ano));
      setErro(null);
    } catch (e) {
      // ARMADILHA 12: erro do usuario aparece na tela, nunca some
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o ano.');
    }
  }, [ano]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro) return <Alerta>{erro}</Alerta>;
  if (!dados) return <p className="text-sm text-tinta-400">Carregando {ano}...</p>;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <Cartao>
          <p className="text-sm text-tinta-400">Entrou no ano</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-tinta-900">
            {reais(dados.totais.entradasCentavos)}
          </p>
        </Cartao>
        <Cartao>
          <p className="text-sm text-tinta-400">Saiu no ano</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-tinta-900">
            {reais(dados.totais.saidasCentavos)}
          </p>
        </Cartao>
        <Cartao>
          <p className="text-sm text-tinta-400">Saldo do ano</p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              dados.totais.saldoCentavos < 0 ? 'text-red-700' : 'text-tinta-900'
            }`}
          >
            {reais(dados.totais.saldoCentavos)}
          </p>
        </Cartao>
      </div>

      <Cartao>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-tinta-900">Mês a mês</h2>
          <div className="flex flex-wrap gap-2">
            <LinkBotao href={urlPrestacaoContas(ano)} target="_blank" rel="noreferrer">
              Prestacao de contas (PDF)
            </LinkBotao>
            <LinkBotao href={urlMovimentoCsv(ano)}>Movimento (CSV)</LinkBotao>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-tinta-200 text-left text-tinta-400">
                <th className="py-2 font-medium">Mês</th>
                <th className="py-2 text-right font-medium">Entrou</th>
                <th className="py-2 text-right font-medium">Saiu</th>
                <th className="py-2 text-right font-medium">Saldo</th>
                <th className="py-2 text-right font-medium">Acumulado</th>
                <th className="py-2 text-right font-medium"> </th>
              </tr>
            </thead>
            <tbody>
              {dados.meses.map((m) => (
                <tr key={m.mes} className="border-b border-tinta-100">
                  <td className="py-2 text-tinta-700">{MESES_CURTO[m.mes - 1]}</td>
                  <td className="py-2 text-right tabular-nums text-tinta-900">
                    {reais(m.entradasCentavos)}
                  </td>
                  <td className="py-2 text-right tabular-nums text-tinta-600">
                    {reais(m.saidasCentavos)}
                  </td>
                  <td
                    className={`py-2 text-right tabular-nums ${
                      m.saldoCentavos < 0 ? 'text-red-700' : 'text-tinta-900'
                    }`}
                  >
                    {reais(m.saldoCentavos)}
                  </td>
                  <td className="py-2 text-right tabular-nums text-tinta-400">
                    {reais(m.acumuladoCentavos)}
                  </td>
                  <td className="py-2 text-right">
                    {m.fechado && <span className="text-xs text-tinta-400">fechado</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Cartao>

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao>
          <h2 className="mb-3 font-semibold text-tinta-900">Entrou por tipo</h2>
          {dados.entradasPorCategoria.length === 0 ? (
            <p className="text-sm text-tinta-400">Nada no ano ainda.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {dados.entradasPorCategoria.map((c) => (
                <li key={c.categoria} className="flex justify-between gap-2">
                  <span className="text-tinta-600">
                    {NOME_CATEGORIA[c.categoria] ?? c.categoria}
                  </span>
                  <span className="tabular-nums text-tinta-900">{reais(c.centavos)}</span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>

        <Cartao>
          <h2 className="mb-3 font-semibold text-tinta-900">Saiu por ministério</h2>
          {dados.saidasPorMinisterio.length === 0 ? (
            <p className="text-sm text-tinta-400">Nada no ano ainda.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {dados.saidasPorMinisterio.map((m) => (
                <li key={m.id ?? 'sem'} className="flex justify-between gap-2">
                  <span className="text-tinta-600">{m.nome}</span>
                  <span className="tabular-nums text-tinta-900">{reais(m.centavos)}</span>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      </div>

      {dados.orcamentos.length > 0 && (
        <Cartao>
          <h2 className="mb-3 font-semibold text-tinta-900">Orcado x realizado no ano</h2>
          <ul className="space-y-2 text-sm">
            {dados.orcamentos.map((o) => {
              const estourou = o.gastoCentavos > o.tetoCentavos;
              return (
                <li key={o.ministerioId} className="flex justify-between gap-2">
                  <span className="text-tinta-600">{o.ministerioNome}</span>
                  <span
                    className={
                      estourou
                        ? 'tabular-nums font-semibold text-red-700'
                        : 'tabular-nums text-tinta-900'
                    }
                  >
                    {reais(o.gastoCentavos)} de {reais(o.tetoCentavos)}
                  </span>
                </li>
              );
            })}
          </ul>
        </Cartao>
      )}
    </div>
  );
}
